import React from 'react'
import clsx from 'clsx'
import { useTheme } from '@mui/material'
import { CloseCircle, Notepad2, Send } from 'iconsax-react'

import Spacer from '../../../../atoms/Spacer'
import CollapseButton from '../../../../atoms/CollapseButton'
import MentionInput from '../../../Mention'
import Button from '../../../../atoms/Button2'
import SendControl from '../../../SendControl'

import { MAX_LENGTH } from './useUserNotes/helpers'
import { isUUID } from '@SonarCX/utilities'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'
import { useEnterMode } from '@SonarCX/providers/EnterModeProvider'
import useUserNotes from './useUserNotes'
import useStyles from './styles'

type KnowledgeSectionProps = {
  user?: APIUser
  canEdit?: boolean
  modal?: boolean
  className?: string
}

// Passing user causes notes to be auto-filled and switches ui to edit mode
const KnowledgeSection: React.FC<KnowledgeSectionProps> = ({
  user,
  canEdit,
  modal,
  className
}) => {
  const theme = useTheme()

  const { down770, up770 } = useBreakpoint()
  const { setAction, setSubtext, setTitle, setV2 } = useAreYouSure()
  const { sendOnEnter, temp, enableTemp, resetTemp } = useEnterMode()
  const { notes, postNote, putNote, deleteNote } = useUserNotes({ user, modal })

  const [expanded, setExpanded] = React.useState(true)
  const [hovering, setHovering] = React.useState('')
  const [editingID, setEditingID] = React.useState('')
  const [editingContent, setEditingContent] = React.useState('')
  const [value, setValue] = React.useState('')
  const isEditing = React.useMemo(() => isUUID(editingID), [editingID])
  const hasUser = React.useMemo(() => Boolean(user), [user])
  const noteArr = React.useMemo(() => Object.entries(notes), [notes])

  const classes = useStyles()

  const handleHover = React.useCallback(
    (key: string, hovering: boolean) => () => {
      if (hovering) {
        setHovering(key)
        return
      }

      setHovering(old => {
        if (old === key) {
          return ''
        } else {
          return old
        }
      })
    },
    []
  )

  // Set a note to be in edit mode
  const handleClickEdit = React.useCallback(
    (key: string) => {
      setEditingID(key)
      setEditingContent(notes[key].content)
    },
    [notes]
  )

  // Clear edit mode and input
  const resetInputState = React.useCallback(() => {
    setEditingID('')
    setEditingContent('')
    setValue('')
  }, [])

  // Save a note change
  const saveEdit = React.useCallback(async () => {
    await putNote(editingID, editingContent)
    resetInputState()
  }, [putNote, editingID, editingContent, resetInputState])

  // Save a new note
  const submit = React.useCallback(async () => {
    await postNote(value)
    resetInputState()
  }, [postNote, value, resetInputState])

  // Ignore an edit
  const discardEdit = React.useCallback(() => {
    resetInputState()
  }, [resetInputState])

  // Handle enter behaviour
  const checkSubmit = React.useCallback(
    e => {
      const { key, shiftKey } = e

      if (key === 'Enter') {
        // Disable send on enter when shift is held once
        if (shiftKey) {
          enableTemp()
          return
        }

        // Prevent sending when disabled
        if (!sendOnEnter || temp) {
          return
        }

        e.preventDefault()
        resetTemp()
        if (isEditing) {
          saveEdit()
        } else {
          submit()
        }
      }

      if (key === 'Escape') {
        discardEdit()
      }
    },
    [
      submit,
      enableTemp,
      resetTemp,
      sendOnEnter,
      temp,
      isEditing,
      saveEdit,
      discardEdit
    ]
  )

  return (
    <div className={clsx(classes.root, className)}>
      <div className={classes.header}>
        <Notepad2
          size={theme.size.base}
          variant='Bold'
          color='grey'
          className='icon'
        />{' '}
        Contact Notes
        {hasUser && (
          <>
            <Spacer />

            <div className='buttons'>
              {up770 && (
                <CollapseButton
                  open={expanded}
                  onToggle={() => setExpanded(old => !old)}
                />
              )}
            </div>
          </>
        )}
      </div>

      {expanded && (
        <div className={classes.inputHolder}>
          {noteArr.map(([key, { content, isOld }], i) => {
            if (!canEdit && !isOld) {
              return null
            }

            return (
              <div
                key={key}
                onMouseEnter={handleHover(key, true)}
                onMouseLeave={handleHover(key, false)}
                onClick={() => handleClickEdit(key)}
                className={classes.item}
                style={{
                  cursor: canEdit ? 'text' : 'default'
                }}
              >
                <div
                  style={{
                    whiteSpace: 'pre-wrap',
                    color: hovering === key ? '#bdbdbd' : '#7F8487',
                    fontSize: theme.size.base,
                    overflowWrap: down770 ? 'anywhere' : 'unset'
                  }}
                >
                  {content}
                </div>

                <div style={{ marginLeft: '0.5rem' }}>
                  {hovering === key && canEdit && (
                    <CloseCircle
                      variant='Outline'
                      size={theme.size.base}
                      onClick={e => {
                        e.stopPropagation()

                        if (!isOld) {
                          deleteNote(key)
                          return
                        }

                        setV2(true)
                        setTitle('DELETE NOTE?')
                        setSubtext('Are you sure you wish to delete this note?')
                        setAction(() => {
                          deleteNote(key)
                        })
                      }}
                      className={classes.delete}
                    />
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <div className={classes.inputContainer}>
        <MentionInput
          focus={isEditing}
          value={isEditing ? editingContent : value}
          onChange={e => {
            const val = e.target.value as string
            if (isEditing) {
              setEditingContent(val)
            } else {
              setValue(val)
            }
          }}
          onKeyDown={checkSubmit}
          placeholder={
            isEditing
              ? 'Edit note...'
              : canEdit
              ? 'Enter note...'
              : "You don't have the permissions to attach a note to this user"
          }
          disabled={!canEdit}
          checkAdd={false}
          disableAgents
          disableTags
          className={classes.noteInput}
          inputClass='text'
          inputProps={{ maxLength: MAX_LENGTH }}
          autoFocus={false}
          isP2P={false}
          type=''
        />

        <div className={classes.buttonHolder}>
          <Button
            onClick={() => {
              resetTemp()
              if (isEditing) {
                saveEdit()
              } else {
                submit()
              }
            }}
            disabled={
              !canEdit || (isEditing ? editingContent : value).length === 0
            }
            className={classes.button}
            endIcon={
              <Send
                size={theme.size.base}
                variant='Bold'
                className={classes.sendIcon}
              />
            }
          >
            {isEditing ? 'EDIT' : 'ADD'}
          </Button>
          <SendControl />
        </div>
      </div>
    </div>
  )
}

export default KnowledgeSection
